import { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, Star } from 'lucide-react';
import SectionTitle from "../SectionTitle";

// Define the testimonial type
interface Testimonial {
  id: number;
  name: string;
  title: string;
  image: string;
  text: string;
  rating: number;
}

const testimonials: Testimonial[] = [
  {
    id: 1,
    name: "Ana Kovač",
    title: "Korekcija nosu",
    image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-4.0.3&auto=format&fit=crop&w=256&q=80",
    text: "Dr. Novak je bil izjemno strokoven in skrben. Rezultati so presenetljivo naravni, natanko takšni, kot sem si želela. Celotna izkušnja je bila veliko boljša, kot sem pričakovala.",
    rating: 5
  },
  {
    id: 2,
    name: "Peter Novak",
    title: "Pomladitev obraza",
    image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=256&q=80",
    text: "Po posegu sem izgledal spočit in mlajši, brez očitnih znakov kirurškega posega. Dr. Novak razume, da je najboljši rezultat tisti, ki izgleda povsem naravno.",
    rating: 5
  },
  {
    id: 3,
    name: "Maja Hrovat",
    title: "Povečanje prsi",
    image: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?ixlib=rb-4.0.3&auto=format&fit=crop&w=256&q=80",
    text: "Od prvega posveta do zadnjega pregleda je bila celotna izkušnja profesionalna. Rezultat je presegel moja pričakovanja, z naravnim videzom in brez komplikacij.",
    rating: 5
  },
  {
    id: 4,
    name: "Andrej Košir",
    title: "Rekonstrukcija roke",
    image: "https://images.unsplash.com/photo-1568602471122-7832951cc4c5?ixlib=rb-4.0.3&auto=format&fit=crop&w=256&q=80",
    text: "Po hudi poškodbi nisem verjel, da bom kdaj ponovno imel polno funkcionalnost roke. Dr. Novak ni le vrhunski kirurg, ampak tudi človek z izjemnim posluhom za paciente.",
    rating: 5
  },
  {
    id: 5,
    name: "Nina Vidmar",
    title: "Korekcija vek",
    image: "https://images.unsplash.com/photo-1580489944761-15a19d654956?ixlib=rb-4.0.3&auto=format&fit=crop&w=256&q=80",
    text: "Dolgo sem razmišljala o tem posegu in dr. Novak me je s svojo strokovnostjo prepričal. Sedaj izgledam bolj spočito, rezultat pa je zelo naraven.",
    rating: 5
  },
  {
    id: 6,
    name: "Matej Zupančič",
    title: "Liposukcija",
    image: "https://images.unsplash.com/photo-1567515004624-219c11d31f2e?ixlib=rb-4.0.3&auto=format&fit=crop&w=256&q=80",
    text: "Odločitev za liposukcijo ni bila lahka, vendar je dr. Novak s svojim pristopom in natančnostjo poskrbel za odlične rezultate. Zelo sem zadovoljen s končnim videzom.",
    rating: 5
  }
];

const Testimonials = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [autoSlideActive, setAutoSlideActive] = useState(true);
  const autoSlideInterval = useRef<NodeJS.Timeout | null>(null);

  // Function to move to the next slide
  const nextSlide = () => {
    setActiveIndex((current) => (current === testimonials.length - 1 ? 0 : current + 1));
  };

  // Function to move to the previous slide
  const prevSlide = () => {
    setActiveIndex((current) => (current === 0 ? testimonials.length - 1 : current - 1));
  };

  // Set up automatic sliding
  useEffect(() => {
    if (autoSlideActive) {
      autoSlideInterval.current = setInterval(() => {
        nextSlide();
      }, 5000); // Change slide every 5 seconds
    }

    return () => {
      if (autoSlideInterval.current) {
        clearInterval(autoSlideInterval.current);
      }
    };
  }, [activeIndex, autoSlideActive]);

  // Pause automatic sliding when user interacts
  const handleManualNavigation = (action: () => void) => {
    if (autoSlideInterval.current) {
      clearInterval(autoSlideInterval.current);
    }
    setAutoSlideActive(false);
    action();
    
    // Resume autoslide after 10 seconds of inactivity
    setTimeout(() => {
      setAutoSlideActive(true);
    }, 10000);
  };

  // Render star rating
  const renderRating = (rating: number) => {
    return Array(5).fill(0).map((_, index) => (
      <Star 
        key={index} 
        size={16} 
        className={`${index < rating ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}`} 
      />
    ));
  };

  return (
    <section className="py-16 md:py-24 bg-gradient-to-br from-medical-50 to-medical-100/50">
      <div className="container mx-auto px-6 md:px-8">
        <SectionTitle
          title="Mnenja naših pacientov"
          subtitle="Preberite, kaj o nas pravijo zadovoljne stranke, in zakaj nam zaupajo svoj videz."
          className="mb-12 md:mb-16"
        />
        
        <div className="max-w-4xl mx-auto relative">
          {/* Current Testimonial */}
          <div className="glass-card p-6 md:p-10 lg:p-12">
            <div className="flex flex-col md:flex-row gap-6 md:gap-8 items-center">
              {/* Image */}
              <div className="w-24 h-24 md:w-28 md:h-28 rounded-full overflow-hidden border-4 border-white/80 shadow-md flex-shrink-0 bg-medical-50">
                <img 
                  src={testimonials[activeIndex].image} 
                  alt={testimonials[activeIndex].name} 
                  className="w-full h-full object-cover"
                />
              </div>
              
              {/* Content */}
              <div className="flex-1">
                <div className="mb-3">
                  {/* Stars */}
                  <div className="flex gap-1 mb-2">
                    {renderRating(testimonials[activeIndex].rating)}
                  </div>
                  
                  {/* Quote */}
                  <p className="italic text-gray-700 text-lg mb-4">"{testimonials[activeIndex].text}"</p>
                  
                  {/* Author */}
                  <div>
                    <p className="font-semibold text-lg text-gray-900">{testimonials[activeIndex].name}</p>
                    <p className="text-medical-600">{testimonials[activeIndex].title}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Navigation Arrows - Outside the card */}
          <button 
            onClick={() => handleManualNavigation(prevSlide)}
            className="absolute top-1/2 -left-5 md:-left-12 transform -translate-y-1/2 w-10 h-10 rounded-full bg-white shadow-md flex items-center justify-center hover:bg-medical-50 transition-colors z-10"
            aria-label="Prejšnje mnenje"
          >
            <ChevronLeft size={20} />
          </button>
          
          <button 
            onClick={() => handleManualNavigation(nextSlide)}
            className="absolute top-1/2 -right-5 md:-right-12 transform -translate-y-1/2 w-10 h-10 rounded-full bg-white shadow-md flex items-center justify-center hover:bg-medical-50 transition-colors z-10"
            aria-label="Naslednje mnenje"
          >
            <ChevronRight size={20} />
          </button>
          
          {/* Dots Indicator */}
          <div className="flex justify-center mt-8 gap-2">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => handleManualNavigation(() => setActiveIndex(index))}
                className={`w-3 h-3 rounded-full ${
                  index === activeIndex ? 'bg-medical-600' : 'bg-medical-200'
                } transition-colors`}
                aria-label={`Pojdi na mnenje ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;